import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from 'yup'
import { Autocomplete, Box, Button, Grid, TextField } from "@mui/material";
import Header from "components/Header";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { BasePathProvider, useBasePath } from "components/Providers";
import { save, destroy } from "store/asset/asset-assign";
import { Controller, useForm } from "react-hook-form";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import _ from 'lodash'
import { useState } from "react";
import Confirm from "components/Dialog";

export default function AddAssetForm({ asset, assetType, vendors, assetCondition }) {
    const { id } = useParams()
    const { t } = useTranslation()
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const basePath = useBasePath()
    const [confirm, setConfirm] = useState(false)

    const schema = yup.object().shape({
        typeId: yup.string(),
        price: yup.number().positive().required(t('Required')),
        life: yup.number()
    })

    const { control, handleSubmit, formState } = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            ...asset,
            vendorId: vendors.find(v => v.name === asset?.vendor?.name)?.id ?? null,
            purchaseDate: asset?.purchaseDate ? dayjs(asset.purchaseDate) : dayjs(),
        }
    })

    const onSubmit = async data => {
        let exceptions = ['assignInfo', 'vendor', 'campus']

        const payload = {
            ...data,
            statusId: id === 'new' ? 'unassigned' : asset?.statusId,
            action: id === 'new' ? 'create' : 'update'
        }

        const res = await dispatch(save(_.omit(payload, exceptions)))
        if (res.payload) {
            navigate(basePath)
        }
    }

    const onDestroy = async () => {
        setConfirm(false)
        await dispatch(destroy({ id: asset.id, action: 'deleted' }))
        navigate(basePath)
    }

    return (
        <BasePathProvider value={`${basePath}`}>
            <Box>
                <Header title={id === 'new' ? t('Add Asset') : t('Edit Asset')} small close="." borderBottom />
            </Box>
            <Box data-component="AssgnAssetForm" component="form" onSubmit={handleSubmit(onSubmit)} py={2} px={1} >
                <Grid container spacing={1.5}>
                    <Grid item xs={12} md={6}>
                        <Controller
                            name="typeId"
                            control={control}
                            render={({ field }) => (
                                <Autocomplete
                                    {...field}
                                    value={assetType.find(option => option.code === field.value) ?? null}
                                    options={assetType}
                                    getOptionLabel={(option) => option.name || ''}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label={t('Type')}
                                            variant="outlined"
                                            fullWidth
                                            size="small"
                                            error={!!formState.errors.typeId}
                                            helperText={formState.errors.typeId ? formState.errors.typeId.message : ''}
                                        />
                                    )}
                                    onChange={(event, newValue) => {
                                        field.onChange(newValue ? newValue.code : null)
                                    }}
                                    onBlur={() => {
                                        field.onBlur()
                                    }}
                                    isOptionDisabled={(option) => option.disabled}
                                    noOptionsText={t('No options')}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Controller
                            name="vendorId"
                            control={control}
                            render={({ field }) => (
                                <Autocomplete
                                    {...field}
                                    value={vendors.find(option => option.id === field.value)}
                                    options={vendors}
                                    getOptionLabel={(option) => option.name || ''}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label={t('Vendor')}
                                            variant="outlined"
                                            fullWidth
                                            size="small"
                                            error={!!formState.errors.typeId}
                                            helperText={formState.errors.typeId ? formState.errors.typeId.message : ''}
                                        />
                                    )}
                                    onChange={(event, newValue) => {
                                        field.onChange(newValue ? newValue.id : null)
                                    }}
                                    onBlur={() => {
                                        field.onBlur()
                                    }}
                                    noOptionsText={t('No options')}
                                />
                            )}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Controller
                            name="brand"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    label={t('Brand')}
                                    variant="outlined"
                                    fullWidth
                                    size="small"
                                    error={!!formState.errors.assetType}
                                    helperText={formState.errors.assetType ? formState.errors.assetType.message : ''}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Controller
                            name="model"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    label={t('Model')}
                                    variant="outlined"
                                    fullWidth
                                    size="small"
                                    error={!!formState.errors.assetType}
                                    helperText={formState.errors.assetType ? formState.errors.assetType.message : ''}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <Controller
                            name="price"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    label={t('Price')}
                                    variant="outlined"
                                    fullWidth
                                    type="number"
                                    size="small"
                                    error={!!formState.errors.assetType}
                                    helperText={formState.errors.assetType ? formState.errors.assetType.message : ''}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <Controller
                            name="life"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    label={t('Life')}
                                    variant="outlined"
                                    fullWidth
                                    type="number"
                                    size="small"
                                    error={!!formState.errors.assetType}
                                    helperText={formState.errors.assetType ? formState.errors.assetType.message : ''}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} md={6} >
                        <Controller
                            name="conditionId"
                            control={control}
                            render={({ field }) => (
                                <Autocomplete
                                    {...field}
                                    value={assetCondition.find(option => option.code === field.value)}
                                    options={assetCondition}
                                    getOptionLabel={(option) => option.name || ''}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label={t('Condition')}
                                            variant="outlined"
                                            fullWidth
                                            size="small"
                                            error={!!formState.errors.typeId}
                                            helperText={formState.errors.typeId ? formState.errors.typeId.message : ''}
                                        />
                                    )}
                                    onChange={(event, newValue) => {
                                        field.onChange(newValue ? newValue.code : null)
                                    }}
                                    onBlur={() => {
                                        field.onBlur()
                                    }}
                                    noOptionsText={t('No options')}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Controller
                            name="assetNumber"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    label={t('Asset Number')}
                                    variant="outlined"
                                    fullWidth
                                    size="small"
                                    error={!!formState.errors.assetType}
                                    helperText={formState.errors.assetType ? formState.errors.assetType.message : ''}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Controller
                            name="serialNumber"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    label={t('Serial Number')}
                                    variant="outlined"
                                    fullWidth
                                    size="small"
                                    error={!!formState.errors.assetType}
                                    helperText={formState.errors.assetType ? formState.errors.assetType.message : ''}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Controller
                            name="purchaseOrder"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    label={t('Purchase Order')}
                                    variant="outlined"
                                    fullWidth
                                    size="small"
                                    error={!!formState.errors.assetType}
                                    helperText={formState.errors.assetType ? formState.errors.assetType.message : ''}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Controller
                            name="purchaseDate"
                            control={control}
                            render={({ field: { value, ...field }, fieldState }) => {
                                return (
                                    <DatePicker
                                        sx={{ width: '100%' }}
                                        slotProps={{ textField: { size: 'small' } }}
                                        label={t('Purchase Date')}
                                        error={!!fieldState.error}
                                        size="small"
                                        value={dayjs(value) || null}
                                        {...field}
                                    />
                                )
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={12}>
                        <Controller
                            name="note"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    label={t('Notes')}
                                    variant="outlined"
                                    fullWidth
                                    multiline
                                    rows={4}
                                    size="small"
                                    error={!!formState.errors.assetType}
                                    helperText={formState.errors.assetType ? formState.errors.assetType.message : ''}
                                />
                            )}
                        />
                    </Grid>
                </Grid>
                <Box display="flex" justifyContent="flex-end" gap={1} mt={2}>
                    {(id !== 'new' && asset.statusId === 'unassigned') && (
                        <Button
                            size="small"
                            variant="contained"
                            color="error"
                            onClick={() => {
                                setConfirm(true)
                            }}
                        >
                            {t('Delete')}
                        </Button>
                    )}

                    <Button
                        type="submit"
                        size="small"
                        variant="contained"
                        color="primary"
                        disabled={!formState.isDirty}
                    >
                        {t('Save')}
                    </Button>
                </Box>
                <Confirm open={confirm} onClose={() => setConfirm(false)} onConfirm={onDestroy} message={`Are you sure you want to delete record?`} />
            </Box>
        </BasePathProvider>
    );
}